import React from 'react';
import { FiImage } from 'react-icons/fi';
import { groupButtonsByRow, parseCaptionNodes } from '../utils/telegramCaptionPreview';
import { getAutoBotMediaDisplayUrl } from '../utils/autoBotMediaUrl';

function PreviewMedia({ item }) {
    const src = getAutoBotMediaDisplayUrl(item);
    if (!src) return null;
    if (item.mediaType === 'video') {
        return (
            <video
                src={src}
                className="h-full w-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
            />
        );
    }
    return <img src={src} alt="" className="h-full w-full object-cover" />;
}

function CaptionNodeView({ node, index }) {
    if (!node) return null;

    if (node.type === 'text') {
        return <span key={index}>{node.value}</span>;
    }

    const children = (node.children || []).map((child, i) => (
        <CaptionNodeView key={`${index}-${i}`} node={child} index={i} />
    ));

    if (node.type === 'b') {
        return <strong key={index} className="font-bold">{children}</strong>;
    }
    if (node.type === 'i') {
        return <em key={index} className="italic">{children}</em>;
    }
    if (node.type === 'u') {
        return <span key={index} className="underline underline-offset-2">{children}</span>;
    }
    if (node.type === 's') {
        return <span key={index} className="line-through opacity-80">{children}</span>;
    }
    if (node.type === 'a') {
        return (
            <a
                key={index}
                href={node.href || '#'}
                title={node.href || ''}
                target="_blank"
                rel="noreferrer"
                className="text-[#6ab3f3] underline decoration-[#6ab3f3]/40 underline-offset-2 hover:decoration-[#6ab3f3]"
                onClick={(e) => e.preventDefault()}
            >
                {children}
            </a>
        );
    }

    return <span key={index}>{children}</span>;
}

const TelegramMessagePreview = ({
    contentMode = 'text_only',
    text = '',
    media = [],
    buttons = [],
    channelName = 'Preview',
    hideButtons = false,
}) => {
    const nodes = parseCaptionNodes(text);
    const rows = hideButtons ? [] : groupButtonsByRow(buttons);
    const firstMedia = media[0];
    const showMedia = contentMode !== 'text_only';

    return (
        <div className="sticky top-4 rounded-xl border border-slate-200 bg-[#0e1621] p-4 text-sm shadow-inner">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Telegram preview
            </p>

            <div className="overflow-hidden rounded-xl bg-[#182533] shadow-lg">
                <div className="flex items-center gap-2 border-b border-white/5 px-3 py-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2b5278] text-xs font-bold text-white">
                        {(channelName || 'C').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-white">{channelName || 'Channel'}</p>
                        <p className="text-[11px] text-slate-400">group · preview</p>
                    </div>
                </div>

                {showMedia ? (
                    media.length === 0 ? (
                        <div className="relative mx-auto aspect-square w-full max-w-[280px] bg-gradient-to-br from-slate-700 to-slate-800">
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-300">
                                <FiImage className="h-14 w-14 opacity-70" aria-hidden="true" />
                                <span className="text-xs uppercase tracking-wider text-slate-400">
                                    {contentMode === 'multimedia' ? 'Album preview' : 'Media preview'}
                                </span>
                            </div>
                        </div>
                    ) : contentMode === 'multimedia' && media.length > 1 ? (
                        <div className="grid grid-cols-2 gap-0.5 bg-black">
                            {media.map((item, idx) => (
                                <div key={`${item.url}-${idx}`} className="relative aspect-square overflow-hidden bg-slate-800">
                                    <PreviewMedia item={item} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="relative mx-auto aspect-square w-full max-w-[280px] overflow-hidden bg-gradient-to-br from-slate-700 to-slate-800">
                            <PreviewMedia item={firstMedia} />
                        </div>
                    )
                ) : null}

                <div className="whitespace-pre-wrap break-words px-3 py-2.5 text-[13px] leading-relaxed text-slate-100">
                    {String(text || '').trim() ? (
                        nodes.map((node, i) => <CaptionNodeView key={`cap-${i}`} node={node} index={i} />)
                    ) : (
                        <span className="italic text-slate-500">
                            {contentMode === 'text_only' ? 'Message preview appears here…' : 'Caption preview appears here…'}
                        </span>
                    )}
                </div>

                {rows.length > 0 ? (
                    <div className="space-y-1.5 border-t border-white/5 px-2 pb-2 pt-2">
                        {rows.map((rowButtons, rowIdx) => (
                            <div key={`row-${rowIdx}`} className="flex flex-wrap gap-1.5">
                                {rowButtons.map((btn) => (
                                    <a
                                        key={`btn-${btn._index}`}
                                        href={btn.url || '#'}
                                        title={btn.url || '(no URL)'}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="min-w-0 flex-1 truncate rounded-md bg-[#2b5278] px-2 py-1.5 text-center text-[12px] font-medium text-white transition hover:bg-[#3a6a94]"
                                        onClick={(e) => e.preventDefault()}
                                    >
                                        {btn.text || 'Button'}
                                    </a>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>

            {hideButtons ? (
                <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
                    Multimedia albums cannot have inline buttons.
                </p>
            ) : (
                <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
                    Hover links/buttons to see URLs. Select text in the editor to Bold / Italic / Link.
                </p>
            )}
        </div>
    );
};

export default TelegramMessagePreview;
